type MusicPlayerOptions = {
    volume?: number;
    loop?: boolean;
};

const clampVolume = (value: number): number => {
    if (Number.isNaN(value)) return 0;
    return Math.min(1, Math.max(0, value));
};

let audio: HTMLAudioElement | null = null;
let loaded = false;
let isPaused = false;

function stopCurrent(): void {
    if (!audio) {
        loaded = false;
        isPaused = false;
        return;
    }

    audio.pause();
    audio.src = "";

    try {
        audio.load();
    } catch {
        // Ignore unsupported browsers.
    }

    audio = null;
    loaded = false;
    isPaused = false;
}

async function safePlay(target: HTMLAudioElement): Promise<void> {
    try {
        await target.play();
    } catch {
        // Ignore autoplay/CORS/media failures. The caller can continue without music.
    }
}

export async function init(url: string, volume: number): Promise<void> {
    if (!url || typeof Audio === "undefined") {
        stopCurrent();
        return;
    }

    stopCurrent();

    const nextAudio = new Audio(url);
    nextAudio.loop = true;
    nextAudio.preload = "auto";
    nextAudio.volume = clampVolume(volume);

    audio = nextAudio;
    loaded = true;
    isPaused = false;

    await safePlay(nextAudio);
}

export function pause(): void {
    if (!audio) {
        return;
    }

    isPaused = true;
    if (!audio.paused) {
        audio.pause();
    }
}

export function unPause(): void {
    if (!audio) {
        return;
    }

    isPaused = false;
    if (audio.paused) {
        void safePlay(audio);
    }
}

export function setVolume(volume: number): void {
    if (!audio) return;
    audio.volume = clampVolume(volume);
}

export function getVolume(): number | null {
    return audio ? audio.volume : null;
}

export function hasAudio(): boolean {
    return !!audio && loaded;
}

export function isPlaying(): boolean {
    return !!audio && !audio.paused && !isPaused;
}
